import { ClaimInfo } from "src/components/shared/card/types";
import { getTitleColor } from "src/components/shared/helper";
import { AnalyticsCard } from "src/services/types";
import { notification } from "antd";
import { isArray, isEmpty } from "lodash";
import { ProcedureTypes } from "src/components/shared/claimProcedure/types";
import type { ClientFilterConfig } from "src/services/types";
import {
  pickDynamicFilterPatch,
  RESERVED_QUEUE_SCALAR_FIELDS,
} from "src/components/shared/dynamicFilters/helpers";

/**
 * Tab / tile filter identity from config.
 * All Claims uses `overjetReviewResult`; Queue uses `filterKey`. Prefer review-result when set.
 */
export const getTabFilterKey = (
  tab?: {
    overjetReviewResult?: string;
    filterKey?: string;
  } | null
): string => tab?.overjetReviewResult ?? tab?.filterKey ?? "";

export const getClaimInfo = (analyticsCard: AnalyticsCard[]) => {
  analyticsCard.sort((a: any, b: any) => a.index - b.index);
  return analyticsCard.map((claim, k: number): ClaimInfo => {
    const status = getTabFilterKey(claim);
    const claimInfo: ClaimInfo = {
      title: claim.title,
      caption: claim?.caption,
      status,
      color: claim?.color || getTitleColor(status),
      count: 0,
      captionCount: 0,
    };
    return claimInfo;
  });
};

export type GetTabsOptions = {
  /**
   * Explicit active tab identifier (Queue: needs_human_rs_review / "").
   * When omitted, All Claims behavior applies: claimTableConfig.filter[0].value
   * (empty filter → All). Do not set this from All Claims.
   */
  activeIdentifier?: string;
  /**
   * When true and no activeIdentifier/filter is set, honor tableTabs[].default.
   * Queue-only — All Claims must leave this unset so behavior stays filter-driven.
   */
  applyConfigDefault?: boolean;
};

/**
 * Builds ClaimTabs menu from front-end `tableTabs` config (All Claims + Queue).
 * Identity: overjetReviewResult (All Claims) or filterKey (Queue); empty = All.
 */
export const getTabs = (
  tableTabs: any[] = [],
  claimTableConfig: any,
  options?: GetTabsOptions
) => {
  let filterConfig: any = [];
  if (
    claimTableConfig &&
    claimTableConfig.filter &&
    Array.isArray(claimTableConfig.filter)
  ) {
    filterConfig = claimTableConfig.filter;
  }

  let activeTabValue = "";
  if (
    options &&
    Object.prototype.hasOwnProperty.call(options, "activeIdentifier")
  ) {
    // Queue (and any caller) explicitly controlling active tab
    activeTabValue = options.activeIdentifier ?? "";
  } else if (filterConfig.length === 0) {
    // All Claims legacy: empty/missing filter → All selected
    activeTabValue = "";
    if (options?.applyConfigDefault) {
      const defaultTab = [...(tableTabs || [])]
        .sort((a: any, b: any) => (a.index ?? 0) - (b.index ?? 0))
        .find((tab: any) => tab.default);
      if (defaultTab) {
        activeTabValue = getTabFilterKey(defaultTab);
      }
    }
  } else {
    activeTabValue = filterConfig[0].value;
  }

  return (tableTabs || [])
    .map((tab: any) => {
      const identifier = getTabFilterKey(tab);
      return {
        index: tab.index,
        title: tab.title,
        identifier,
        active: activeTabValue === identifier,
        color: tab.color ? tab.color : getTitleColor(identifier),
        overjetClientReviewCount: 0,
        count: 0,
      };
    })
    .sort((a: any, b: any) => a.index - b.index);
};

export const getUpdatedTabs = (
  statsByReview: any,
  tabsUpdate: any[],
  reviewsTotal: any
) => {
  return tabsUpdate.map((tab) => {
    const keyToMatch = tab.identifier === "" ? "Total" : tab.identifier;
    if (typeof statsByReview[keyToMatch] === "object") {
      const { overjetClientReviewCount = 0, overjetReviewResultCount = 0 } =
        statsByReview[keyToMatch] || {};
      if (tab.active) {
        reviewsTotal.totalReivews = overjetClientReviewCount;
        reviewsTotal.activeTabTitle = tab.title.toLowerCase();
      }
      return {
        ...tab,
        count: overjetReviewResultCount,
        overjetClientReviewCount,
      };
    }
    return {
      ...tab,
      count: statsByReview[keyToMatch],
    };
  });
};

export const routeToLastReviewedClaim = (
  lastViewedClaims: any,
  claimTabs: any[],
  history: any
) => {
  const lastClaim = lastViewedClaims?.userSetting?.lastViewedClaimLineItems;
  if (isArray(lastClaim) && lastClaim.length > 0) {
    const activeTab = claimTabs.find((tab: any) => tab.active === true);
    const claimAsPerActiveTab = lastClaim.find(
      (claim: any) =>
        claim.identifier === activeTab?.identifier ||
        claim.identifier === activeTab?.title.toUpperCase()
    );
    if (!claimAsPerActiveTab) {
      notification.info({ message: "No last reviewed claim found." });
      return;
    }
    const {
      id: rid,
      procedure: pCode,
      overjetReviewResult,
      claimId,
      pendCode,
      arch,
      quadrant,
      toothNumber,
    } = claimAsPerActiveTab.claimLineItem;
    const claimIndex = "0"; //getclaimIndex(rid, record, claimsConfig);
    let search: any = new URLSearchParams({
      rid,
      pCode,
      overjetReviewResult,
      claimId,
      claimIndex,
      totalClaims: "0",
    });
    if (pCode.includes("D5")) {
      search.append("arch", arch);
    } else if (pCode.includes("D4")) {
      search.append("q", quadrant);
    } else {
      search.append("claimTooth", toothNumber);
    }
    if (pendCode) {
      search.append("pendCode", pendCode);
    }
    search = search.toString();
    history.push(`/canvas?${search}`);
  }
};

export function normalizeClaimData(claimsTable: any, claimData: any) {
  const { claimLevel } = claimsTable || {};
  let normalizedData: any = {};
  if (claimLevel) {
    normalizedData = { claims: claimData?.claimLevel };
  } else {
    normalizedData = {
      claims: claimData?.homePageClaims
        ? claimData.homePageClaims
        : claimData?.claims,
    };
  }
  return normalizedData;
}

export const getCardsWithCount = (
  cards: ClaimInfo[],
  statsByReviewResultAggregated: any
) => {
  return cards.map((claimInfo, k: number): ClaimInfo => {
    if (
      statsByReviewResultAggregated &&
      !isEmpty(statsByReviewResultAggregated) &&
      claimInfo.status &&
      statsByReviewResultAggregated[claimInfo.status]
    ) {
      if (
        typeof statsByReviewResultAggregated[claimInfo?.status] === "object"
      ) {
        const { overjetClientReviewCount, overjetReviewResultCount } =
          statsByReviewResultAggregated[claimInfo?.status];
        claimInfo.count = overjetReviewResultCount;
        claimInfo.captionCount = overjetClientReviewCount;
      } else {
        claimInfo.count =
          typeof statsByReviewResultAggregated[claimInfo?.status] === "number"
            ? statsByReviewResultAggregated[claimInfo?.status]
            : 0;
      }
    }
    return claimInfo;
  });
};

export const getConfigLocal = (
  patch: any,
  procedureType: ProcedureTypes,
  homePageFilters?: Record<string, ClientFilterConfig>
) => {
  return {
    startDate: patch.startDate,
    endDate: patch.endDate,
    sort: patch.sort,
    filter: patch.filter || [],
    search: patch.search,
    procedureType: procedureType.currentSelectedProcedure,
    systemFilter: patch.systemFilter,
    dateRangeFilterField: patch.dateRangeFilterField,
    ...pickDynamicFilterPatch(
      patch,
      homePageFilters,
      RESERVED_QUEUE_SCALAR_FIELDS
    ),
  };
};
